import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Users, FileText, Target, Globe2, Compass, Video, Map, Layers, Send, CheckCircle2, Mountain, Waves, Cloud, TreePine } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useSiteContent } from '../contexts/SiteContentContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Local Object Storage URLs (no external dependencies)
const LOCAL = {
  ocean_hero: `${API}/api/files/dersimizcografiya/uploads/806c928c-e8dc-4a8b-987d-2e58f21fb82d.jpg`,
  mountain: `${API}/api/files/dersimizcografiya/uploads/47ef7813-7734-4f04-bad2-41faed93bdbd.jpg`,
  desert: `${API}/api/files/dersimizcografiya/uploads/8323a863-2eaa-456c-8e55-ee3c07bfd141.jpg`,
  globe: `${API}/api/files/dersimizcografiya/uploads/f959a2d9-7cfe-459c-b0bf-42d8f29bae91.jpg`,
  ocean_waves: `${API}/api/files/dersimizcografiya/uploads/6c1e8512-f1b6-40d6-8b84-3c9e14379c1a.jpg`,
  aurora: `${API}/api/files/dersimizcografiya/uploads/7b1de8e6-cf40-4f99-be57-48941bc4ef9a.jpg`,
  river: `${API}/api/files/dersimizcografiya/uploads/ef2756c6-5b2c-4b74-a57a-2a8e9f5b7a0c.jpg`,
  volcano: `${API}/api/files/dersimizcografiya/uploads/af46362f-3e81-4e09-89e3-5da894bd82b9.jpg`,
  mission_bg: `${API}/api/files/dersimizcografiya/uploads/d6ce482f-a305-43fa-b24a-98d96d8686d5.jpg`,
  newsletter_bg: `${API}/api/files/dersimizcografiya/uploads/1e932707-947b-4606-a336-926286aca6bc.jpg`,
  categories_bg: `${API}/api/files/dersimizcografiya/uploads/3f616866-23cf-499b-b51c-b512c4c56a53.jpg`,
};

const iconMap = { BookOpen, Users, FileText, Target, Mountain, Map, Layers, Waves, Cloud, TreePine, Globe2, Compass, Video };

function AnimatedCounter({ value, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting && !started) setStarted(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const num = parseInt(value) || 0;
    let current = 0;
    const step = Math.ceil(num / 120) || 1;
    const timer = setInterval(() => { current += step; if (current >= num) { setCount(num); clearInterval(timer); } else setCount(current); }, 16);
    return () => clearInterval(timer);
  }, [started, value]);
  return <span ref={ref}>{count}{suffix}</span>;
}

export default function Home() {
  const content = useSiteContent();
  const [subEmail, setSubEmail] = useState('');
  const [subStatus, setSubStatus] = useState('');

  if (!content) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/api/subscribe`, { email: subEmail }); setSubStatus('success'); setSubEmail(''); }
    catch { setSubStatus('error'); }
  };

  // Read from CMS with fallbacks
  const h = content.hero || {};
  const heroTitle1 = h.title1 || 'Keyfiyyətli';
  const heroTitle2 = h.title2 || 'Coğrafiya Təhsili';
  const heroSubtitle = h.subtitle || 'Dağlardan okeanlara, atmosferdən kainata — müasir metodlarla coğrafiya dünyasını kəşf edin';
  const heroTeacher = h.teacher_name || 'İsfəndiyar İsgəndərov';
  // CMS helper: undefined=use default, ""=intentionally empty, "url"=use url
  const img = (val, def) => val === undefined ? def : val;
  
  const heroBg = img(h.bg_image, LOCAL.ocean_hero);
  const heroBadge = h.badge || 'dersimizcografiya.az';
  const heroBtn1 = h.btn1 || 'Xidmətlər';
  const heroBtn2 = h.btn2 || 'Yeniliklər';
  const heroBtn1Url = h.btn1_url || '/services';
  const heroBtn2Url = h.btn2_url || '/news';
  const stats = [
    { value: (h.stat1_value || '15').replace(/[^0-9]/g,''), suffix: (h.stat1_value||'15+').replace(/[0-9]/g,''), label: h.stat1_label || 'İl Təcrübə' },
    { value: (h.stat2_value || '500').replace(/[^0-9]/g,''), suffix: (h.stat2_value||'500+').replace(/[0-9]/g,''), label: h.stat2_label || 'Şagird' },
    { value: (h.stat3_value || '50').replace(/[^0-9]/g,''), suffix: (h.stat3_value||'50+').replace(/[0-9]/g,''), label: h.stat3_label || 'Kurs' },
    { value: (h.stat4_value || '95').replace(/[^0-9]/g,''), suffix: (h.stat4_value||'95%').replace(/[0-9]/g,''), label: h.stat4_label || 'Uğur Nisbəti' },
  ];

  const svc = content.services_section || {};
  const svcDefaults = [
    {title:'Fərdi Dərslər',desc:'Hər şagirdin ehtiyacına uyğun plan',icon:'BookOpen'},
    {title:'Qrup Dərsləri',desc:'Kiçik qruplarla interaktiv öyrənmə',icon:'Users'},
    {title:'Dərs Vəsaitləri',desc:'PDF, video və interaktiv materiallar',icon:'FileText'},
    {title:'İmtahana Hazırlıq',desc:'Qəbul və buraxılış hazırlığı',icon:'Target'},
  ];
  const serviceItems = (svc.items || []).map((item, i) => ({
    title: item.title || svcDefaults[i]?.title || '',
    desc: item.desc || svcDefaults[i]?.desc || '',
    icon: item.icon || svcDefaults[i]?.icon || 'BookOpen',
  }));
  if (serviceItems.length === 0) serviceItems.push(...svcDefaults);
  const svcIcons = [BookOpen, Users, FileText, Target];

  const cat = content.categories_section || {};
  const catTitle = cat.title || 'Coğrafiya Dünyası';
  const catSubtitle = cat.subtitle || 'Hər mövzuda peşəkar dərs materialları';
  const catBgImage = img(cat.bg_image, LOCAL.categories_bg);
  const defCats = [
    {title:'Fiziki Coğrafiya',desc:'Relyef, iqlim, su ehtiyatları',icon:'Mountain',image:LOCAL.mountain},
    {title:'İqtisadi Coğrafiya',desc:'Əhali, təsərrüfat, dünya ölkələri',icon:'Map',image:LOCAL.desert},
    {title:'Xəritəşünaslıq',desc:'Xəritə oxuma, koordinat sistemi',icon:'Layers',image:LOCAL.globe},
    {title:'Okeanologiya',desc:'Okean və dənizlər, su axınları',icon:'Waves',image:LOCAL.ocean_waves},
    {title:'Meteorologiya',desc:'Atmosfer hadisələri, hava proqnozu',icon:'Cloud',image:LOCAL.aurora},
    {title:'Biocoğrafiya',desc:'Təbii zonalar, flora və fauna',icon:'TreePine',image:LOCAL.river},
  ];
  const catItems = (cat.items || []).map((c, i) => ({
    title: c.title || defCats[i]?.title || '',
    desc: c.desc || defCats[i]?.desc || '',
    icon: c.icon || defCats[i]?.icon || 'Globe2',
    image: c.image || defCats[i]?.image || '',
    url: c.url || `/category/${i}`,
  }));
  if (catItems.length === 0) catItems.push(...defCats);
  const catIcons = [Mountain, Map, Layers, Waves, Cloud, TreePine];

  const ab = content.about || {};
  const aboutBadge = ab.badge || 'Haqqımızda';
  const aboutTitle = ab.title || 'Biz Kimik?';
  const aboutDesc = ab.description || 'Peşəkar coğrafiya müəllimi olaraq şagird və tələbələrə keyfiyyətli təhsil xidməti göstəririk. Uzun illər təcrübəmizlə coğrafiyanı maraqlı və əyləncəli şəkildə öyrədirik.';
  const aboutImage = img(ab.image, LOCAL.globe);
  const aboutYear = ab.year || '2011';
  const aboutQuality = ab.quality || '99%';
  const aboutSuccess = ab.success || '95%';
  const aboutVolcano = img(ab.volcano_image, LOCAL.volcano);
  const aboutBtnText = ab.btn_text || 'Ətraflı Oxu';
  const aboutBtnUrl = ab.btn_url || '/about';
  const catBadge = cat.badge || 'Dərs Kateqoriyaları';

  const ms = content.mission_section || {};
  const msBg = img(ms.bg_image, LOCAL.mission_bg);
  const msTitle = ms.section_title || 'Dağlar Qədər Möhkəm Dəyərlər';
  const msMissionBadge = ms.mission_badge || 'Missiyamız';
  const msMissionTitle = ms.mission_title || 'Bizim Missiyamız';
  const msMissionText = ms.mission_text || 'Şagird və tələbələrə ən keyfiyyətli coğrafiya təhsili verərək, onların dünyamızı daha yaxşı anlamalarına töhfə vermək.';
  const msVisionBadge = ms.vision_badge || 'Vizyonumuz';
  const msVisionTitle = ms.vision_title || 'Bizim Vizyonumuz';
  const msVisionText = ms.vision_text || 'Azərbaycanda coğrafiya təhsilinin inkişafına töhfə verən, müasir texnologiyalarla dəstəklənən ən etibarlı platform olmaq.';

  const nl = content.newsletter_section || {};
  const nlTitle = nl.title || 'Abunə ol!';
  const nlSubtitle = nl.subtitle || 'Yeni dərs materialları və yeniliklərdən xəbərdar olun';
  const nlBg = img(nl.bg_image, LOCAL.newsletter_bg);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0"><img src={heroBg} alt="" className="w-full h-full object-cover scale-110 ocean-bg" /></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#03045E]/30 via-transparent to-[#03045E]/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}>
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-bold mb-8 border border-white/20">
              <Globe2 className="w-4 h-4" /> {heroBadge}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              <span className="block">{heroTitle1}</span>
              <span className="bg-gradient-to-r from-[#48CAE4] via-[#90E0EF] to-[#00B4D8] bg-clip-text text-transparent">{heroTitle2}</span>
            </h1>
            <p className="text-lg md:text-xl text-white font-semibold max-w-2xl mx-auto mb-8 leading-relaxed">
              {heroSubtitle.includes('{teacher}') && heroTeacher ? (
                heroSubtitle.split('{teacher}').map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && <span className="text-[#FFD700] font-bold">{heroTeacher}</span>}
                  </React.Fragment>
                ))
              ) : heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={heroBtn1Url} data-testid="hero-cta-resources" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white text-[#0077B6] font-bold text-base hover:bg-white/90 transition-all duration-500 shadow-xl hover:shadow-2xl group">
                {heroBtn1} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to={heroBtn2Url} data-testid="hero-cta-contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white/10 backdrop-blur-sm text-white font-bold text-base hover:bg-white/20 transition-all duration-500 border border-white/30">
                {heroBtn2}
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-4xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 text-center hover:bg-white/15 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-bold text-white"><AnimatedCounter value={stat.value} suffix={stat.suffix} /></div>
                <div className="text-xs text-white/70 mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <motion.div className="absolute bottom-24 left-10 hidden lg:block" animate={{ y: [0, -15, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}><Mountain className="w-12 h-12 text-white/15" /></motion.div>
        <motion.div className="absolute top-40 right-16 hidden lg:block" animate={{ y: [0, 10, 0], rotate: [0, 5, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}><Compass className="w-10 h-10 text-white/10" /></motion.div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#0077B6] via-[#00B4D8] to-[#0077B6]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {serviceItems.map((item, i) => {
              const Icon = iconMap[item.icon] || svcIcons[i] || BookOpen;
              return (
                <motion.div key={i} className="group text-center p-6 rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-500 card-hover border border-[#E2E8F0]"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-[#0077B6] to-[#00B4D8] mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1A2530] mb-1">{item.title}</h3>
                  <p className="text-sm text-[#5C6B7A]">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-br from-slate-800/80 via-[#03045E]/70 to-slate-800/80">
        <div className="absolute inset-0 opacity-20"><img src={catBgImage} alt="" className="w-full h-full object-cover" /></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white text-sm font-bold mb-4 border border-white/20">{catBadge}</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">{catTitle}</h2>
            <p className="text-xl text-white font-semibold max-w-2xl mx-auto">{catSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {catItems.map((c, i) => {
              const Icon = iconMap[c.icon] || catIcons[i] || Globe2;
              return (
                <motion.div key={i} className="group relative overflow-hidden rounded-3xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 card-hover"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={c.image || LOCAL.mountain} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <div className="absolute top-4 right-4 p-3 rounded-2xl bg-white/20 backdrop-blur-sm"><Icon className="w-6 h-6 text-white" /></div>
                    <div className="absolute bottom-4 left-6"><h3 className="text-xl font-bold text-white">{c.title}</h3></div>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-[#5C6B7A] mb-4">{c.desc}</p>
                    <Link to={c.url || `/category/${i}`} className="inline-flex items-center bg-gradient-to-r from-[#0077B6] to-[#00B4D8] bg-clip-text text-transparent font-semibold text-sm group-hover:gap-2 transition-all">
                      Ətraflı <ArrowRight className="ml-1 w-4 h-4 text-[#0077B6] group-hover:translate-x-2 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="absolute -right-40 -bottom-40 w-[500px] h-[500px] rounded-full bg-[#0077B6]/5" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl"><img src={aboutImage} alt="Globe" className="w-full h-[420px] object-cover" /></div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-xl border border-[#E2E8F0]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0077B6] to-[#00B4D8] flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-white" /></div>
                  <div><div className="text-2xl font-bold text-[#0077B6]">{aboutSuccess}</div><div className="text-xs text-[#5C6B7A]">uğur nisbəti</div></div>
                </div>
              </div>
              <div className="absolute -top-8 -left-8 w-24 h-24 rounded-2xl overflow-hidden shadow-lg hidden md:block">
                <img src={aboutVolcano} alt="" className="w-full h-full object-cover" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="inline-block px-4 py-2 rounded-full bg-[#0077B6]/10 text-[#0077B6] text-sm font-bold border border-[#0077B6]/20">{aboutBadge}</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1A2530] mt-4 mb-6">
                <span className="bg-gradient-to-r from-[#0077B6] to-[#00B4D8] bg-clip-text text-transparent">{aboutTitle}</span>
              </h2>
              <p className="text-[#5C6B7A] leading-relaxed mb-6 text-lg">{aboutDesc}</p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gradient-to-br from-[#0077B6]/5 to-[#00B4D8]/5 rounded-2xl p-5 border border-[#0077B6]/10">
                  <div className="text-3xl font-bold text-[#0077B6]">{aboutYear}</div><div className="text-xs text-[#5C6B7A] mt-1">fəaliyyət ili</div>
                </div>
                <div className="bg-gradient-to-br from-[#00B4D8]/5 to-[#48CAE4]/5 rounded-2xl p-5 border border-[#00B4D8]/10">
                  <div className="text-3xl font-bold text-[#0077B6]">{aboutQuality}</div><div className="text-xs text-[#5C6B7A] mt-1">keyfiyyət</div>
                </div>
              </div>
              <Link to={aboutBtnUrl} data-testid="home-about-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-[#0077B6] to-[#00B4D8] text-white font-semibold hover:shadow-lg transition-all duration-300 group">
                {aboutBtnText} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MISSION/VISION */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0">{msBg ? <img src={msBg} alt="" className="w-full h-full object-cover" /> : null}<div className="absolute inset-0 bg-[#03045E]/30" /></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12"><Mountain className="w-12 h-12 text-white/30 mx-auto mb-4" /><h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{msTitle}</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-10 border border-white/20" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-white text-sm font-bold mb-4 border border-white/20">{msMissionBadge}</span>
              <h3 className="text-2xl font-bold text-white mb-4">{msMissionTitle}</h3>
              <p className="text-white/70 leading-relaxed">{msMissionText}</p>
            </motion.div>
            <motion.div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-10 border border-white/20" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-white text-sm font-bold mb-4 border border-white/20">{msVisionBadge}</span>
              <h3 className="text-2xl font-bold text-white mb-4">{msVisionTitle}</h3>
              <p className="text-white/70 leading-relaxed">{msVisionText}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">{nlBg ? <img src={nlBg} alt="" className="w-full h-full object-cover" /> : null}<div className="absolute inset-0 bg-gradient-to-r from-[#0077B6]/80 to-[#00B4D8]/80" /></div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Compass className="w-10 h-10 text-white/60 mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{nlTitle}</h2>
            <p className="text-white/80 mb-8 text-lg">{nlSubtitle}</p>
            {subStatus === 'success' ? (
              <div className="flex items-center justify-center gap-2 text-white" data-testid="subscribe-success"><CheckCircle2 className="w-5 h-5" /> Abunəliyiniz uğurla qeydə alındı!</div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="subscribe-form">
                <Input data-testid="subscribe-email" type="email" value={subEmail} onChange={(e) => setSubEmail(e.target.value)} placeholder="E-mail ünvanınız" required className="flex-1 bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:bg-white/30 h-12 rounded-2xl" />
                <Button data-testid="subscribe-submit" type="submit" className="bg-white text-[#0077B6] hover:bg-white/90 font-bold px-8 h-12 shadow-lg rounded-2xl">Abunə ol</Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
